<template>
  <bryntum-scheduler-pro-project-model
    ref="project"
    :autoLoad="autoLoad"
    :autoSync="autoSync"
    :writeAllFields="true"
    :eventStore="eventStore"
    :resourceStore="resourceStore"
    :assignmentStore="assignmentStore"
    :timeRangeStore="timeRangeStore"
    :phantomIdField="phontomIdField"
    :listeners="listeners"
    :transport="transport"
    :stm="stm"
  />
</template>
<script>
import { BryntumSchedulerProProjectModel } from "@bryntum/schedulerpro-vue";
import { StateTrackingManager } from "@bryntum/schedulerpro";
import moment from "moment-timezone";
import { ref } from "vue";

import Store from "@/store/store";

export default {
  name: "SchedulerProjectModel",
  props: {
    // 自動同期フラグ
    // こちらはtureにすると、イベントの変更後に他イベントを調整する際、イベントのstartDate, endDateが変更できないことがあったのでfalseにしている。
    // サーバとの同期はhasChangesイベントの最後にsync関数を明示的に呼び出して行なっている・
    autoSync: {
      type: Boolean,
      default: false,
    },
    autoLoad: {
      type: Boolean,
      default: false,
    },
    transport: {
      type: Object,
      default: () => ({
        load: {
          url: "",
        },
        sync: {
          url: "",
        },
      }),
    },
    // 年度
    fiscalYear: {
      type: Number,
      default: undefined,
    },
    eventStore: {
      type: Object,
      default: null,
      required: true,
    },
    resourceStore: {
      type: Object,
      default: null,
      required: true,
    },
    assignmentStore: {
      type: Object,
      default: null,
      required: true,
    },
    timeRangeStore: {
      type: Object,
      default: null,
      required: true,
    },
    // スケジューラのイベントに変更があった場合に呼び出されるコールバック関数
    // サーバー同期の前に呼び出される。
    hasScheduleChangedAsync: {
      type: Function,
      default: () => {},
    },
    // サーバー同期が成功した場合に呼び出されるコールバック関数
    syncSuccessAsync: {
      type: Function,
      default: async () => {},
    },
    // サーバー同期が失敗した場合に呼び出されるコールバック関数
    syncFailAsync: {
      type: Function,
      default: async response => {},
    },
    // ロード失敗時に呼び出されるコールバック関数
    loadFailAsync: {
      type: Function,
      default: async response => {},
    },
  },
  components: {
    BryntumSchedulerProProjectModel,
  },
  setup(props) {
    const project = ref(null);
    const phontomIdField = "phantom_id";
    const listeners = {
      load: async () => {
        project.value.instance.resetQueue();
      },
      /**
       * スケジューラのイベントに変更があった場合に呼び出される。
       * @param param0
       */
      hasChanges: async ({ source: project }) => {
        const changesEvents = project.changes.events;

        if (changesEvents === undefined) {
          return;
        }

        // 変更の追跡を一時停止する。
        project.suspendChangeTracking();

        // イベントストア、変更差分を取得
        const eventStore = project.eventStore;
        const changes = eventStore.changes;

        // スケジューラのイベントに変更があった場合に呼び出されるコールバック関数
        await props.hasScheduleChangedAsync(project, changes);

        // サーバ同期
        project.sync();
      },
      /**
       * サーバ同期前イベント
       * @param data
       */
      beforesync: async data => {
        let { pack, source: project } = data;

        // 同期前にAiseaAPIのid_tokenを設定する
        project.transport.load.headers.id_token = Store.state.auth.idToken;

        const toUTCFromat = date => {
          return moment(date)
            .utc()
            .format();
        };

        const events = pack.events;

        // 送信するデータの日時をUTCに変換
        if (events.updated) {
          events.updated.forEach(record => {
            record.start_date = toUTCFromat(record.start_date);
            record.end_date = toUTCFromat(record.end_date);
            // 更新時は役職ポジションIDは送らない
            delete record.enterprise_manning_vessel_role_position_id;
          });
        }

        if (events.added) {
          events.added.forEach(record => {
            record.start_date = toUTCFromat(record.start_date);
            record.end_date = toUTCFromat(record.end_date);
          });
        }

        // 削除されたイベント、アサインメントにバージョン情報を追加
        const eventChanges = project.eventStore.changes;
        if (events.removed && eventChanges && eventChanges.removed) {
          events.removed = eventChanges.removed.map(record => {
            return {
              id: record.id,
              version: record.version,
              event_type: record.event_type,
              enterprise_manning_vessel_crew_id:
                record.enterprise_manning_vessel_crew_id,
            };
          });
        }

        const assignmentStore = project.assignmentStore;
        const assignmentChanges = assignmentStore.changes;
        if (
          pack &&
          pack.assignments &&
          pack.assignments.removed &&
          assignmentChanges &&
          assignmentChanges.removed
        ) {
          pack.assignments.removed = assignmentStore.removed.map(record => {
            return {
              id: record.id,
              version: record.version,
            };
          });
        }

        pack.fiscal_year = props.fiscalYear;
      },
      /**
       * ロード前イベント
       * @param param0
       */
      beforeLoad: async ({ source: project }) => {
        // 読み込み前にAiseaAPIのid_tokenを設定する
        project.transport.load.headers.id_token = Store.state.auth.idToken;
      },
      /**
       * ロード失敗イベント
       */
      loadFail: async ({ response }) => {
        // ロード失敗後、親イベント呼び出し
        await props.loadFailAsync(response);
      },
      /**
       * 同期失敗イベント
       */
      syncFail: async ({ response }) => {
        // 通信したタイミングでhasChangesを再開する。
        project.value.instance.resumeChangeTracking(
          true /* 停止していた期間のhasChangesイベントはスキップ */
        );
        // 同期失敗後、親イベント呼び出し
        await props.syncFailAsync(response);
      },
      /**
       * 同期成功イベント
       */
      sync: async ({ response }) => {
        // 通信したタイミングでhasChangesイベントを再開する。
        project.value.instance.resumeChangeTracking(
          true /* 停止していた期間のhasChangesイベントはスキップ */
        );

        // レスポンスのアラート情報をイベントに設定
        if (
          response &&
          response.events &&
          Array.isArray(response.events.rows)
        ) {
          const eventStore = project.value.instance.eventStore;
          response.events.rows.forEach(row => {
            const event = eventStore.getById(row.id);
            if (event) {
              event.set("alerts", row.alerts);
            }
          });
        }
        project.value.instance.eventStore.commit(true /* silent */);

        // 同期成功後、親イベント呼び出し
        await props.syncSuccessAsync(response);
      },
    };

    const stm = new StateTrackingManager({
      autoRecord: true,
      disabled: false,
    });

    return {
      project,
      phontomIdField,
      listeners,
      stm,
    };
  },
  mounted() {
    this.project = this.$refs.project;
  },
};
</script>
